import { Component } from '@theme/component';

/**
 * Clamps a cart line's variant and property details to a few lines in the cart drawer, and reveals an
 * overlaid toggle only when the content actually overflows.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content - The variant, property, and selling plan details.
 * @property {HTMLButtonElement} toggle - The show more / show less button.
 *
 * @extends {Component<Refs>}
 */
class CartLineOptions extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {number} */
  #animationFrame = 0;

  #resizeObserver = new ResizeObserver(() => this.#scheduleMeasure());

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    cancelAnimationFrame(this.#animationFrame);
  }

  /**
   * Expands or collapses the details.
   */
  toggleExpanded() {
    const expanded = !this.hasAttribute('expanded');

    this.toggleAttribute('expanded', expanded);
    this.refs.toggle.setAttribute('aria-expanded', String(expanded));
    if (!expanded) this.#scheduleMeasure();
  }

  #observe() {
    this.#resizeObserver.disconnect();
    this.#resizeObserver.observe(this.refs.content);
    this.#scheduleMeasure();
  }

  #scheduleMeasure() {
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = requestAnimationFrame(() => this.#measure());
  }

  #measure() {
    const { content, toggle } = this.refs;

    if (this.hasAttribute('expanded')) return;

    // Clamp first, then compare the clamped box against the full content height.
    this.setAttribute('collapsible', '');
    const overflowing = content.scrollHeight > content.clientHeight + 1;

    this.toggleAttribute('collapsible', overflowing);
    toggle.hidden = !overflowing;
    toggle.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('cart-line-options')) {
  customElements.define('cart-line-options', CartLineOptions);
}
